/**
 * ventaHistorialLinks.ts — Rutas de deep-link al historial de ventas (lib).
 *
 * QUÉ HACE: Centraliza la URL para abrir un ticket concreto (`?ventaId=`) en
 * /caja/ventas, reutilizada desde cierre de caja, inventario (auditoría de
 * stock), auditoría y métricas.
 *
 * Vive en src/lib/ porque lo consumen varias features sin importar el interior
 * de `features/caja` (la regla de aislamiento solo permite el service público).
 *
 * NO debe contener lógica de React Router ni efectos — solo construcción de paths.
 */

/** YYYY-MM-DD desde ISO o fecha local. */
export const fechaContableFromIso = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export type VentaHistorialLinkOptions = {
  ventaId?: string;
  /** YYYY-MM-DD; si falta y hay ventaId, el hook puede resolverla al cargar ventas. */
  fecha?: string;
  tab?: "ventas" | "cierres" | "movimientos";
  /** Abre comprobante del movimiento en pestaña Movimientos. */
  movimientoId?: string;
  /** Filtra el historial por nombre de producto (consolidado del cierre → ventas). */
  producto?: string;
};

export type CreditoHistorialLinkOptions = {
  creditoId: string;
  /** Abre comprobante del abono en historial → Movimientos. */
  movimientoId?: string;
};

export function buildVentaHistorialSearchParams(
  opts: VentaHistorialLinkOptions = {},
): string {
  const params = new URLSearchParams();
  params.set("tab", opts.tab ?? "ventas");
  if (opts.fecha) params.set("fecha", opts.fecha);
  if (opts.ventaId) params.set("ventaId", opts.ventaId);
  if (opts.movimientoId) params.set("movimientoId", opts.movimientoId);
  if (opts.producto) params.set("q", opts.producto);
  return params.toString();
}

export function buildCreditoHistorialSearchParams(
  opts: CreditoHistorialLinkOptions,
): string {
  const params = new URLSearchParams();
  params.set("creditoId", opts.creditoId);
  if (opts.movimientoId) params.set("movimientoId", opts.movimientoId);
  return params.toString();
}

export function buildCreditoHistorialPath(
  opts: CreditoHistorialLinkOptions,
): string {
  return `/caja/creditos?${buildCreditoHistorialSearchParams(opts)}`;
}

export function buildVentaHistorialPath(
  opts: VentaHistorialLinkOptions = {},
): string {
  return `/caja/ventas?${buildVentaHistorialSearchParams(opts)}`;
}

/** Fecha contable YYYY-MM-DD desde ISO de venta o movimiento de stock. */
export function ventaHistorialFechaFromIso(iso: string): string {
  return fechaContableFromIso(iso);
}
